<script setup lang="ts">
import { ref } from "vue";
import PrimaryButton from "../utils/PrimaryButton/PrimaryButton.vue";
import Callout from "../utils/Callout.vue";
import useAosRefresh from "@/composables/useAosRefresh";
import {useI18n} from "vue-i18n";
const {t} = useI18n({
  useScope: 'global'
});

export interface Qa {
  question: string
  answer: string // HTML.
}

export interface Props {
  qas: Qa[]
}

useAosRefresh()

const props = defineProps<Props>()

if(!props.qas?.length) throw new Error('No FAQ question/answers provided, but the FAQ component is being used.')

const selectedFaq = ref<number>(0);

function selectFaq(index: number) {
  // if (index === selectedFaq.value) {

  //   selectedFaq.value = -1;

  //   return;
  // }

  selectedFaq.value = index;
}

</script>

<template>
  <section 
    data-aos
    :class="[
      'container',
      'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0)'
    ]"
  >
    <Callout icon="i-mingcute:performance-line"> {{ t('proven_to_perform') }} </Callout>
    <div class="lg:flex-row flex flex-col gap-x-12 mt-3.5 relative">
      <div class="basis-1/2 flex flex-col justify-between gap-y-10">
        <div>
          <h2 class="heading-2">
            {{  t('faq') }}
          </h2>
          <p class="text-brand-secondary leading-[150%] max-w-[104] mt-6">
            {{  t('faq_desc') }}
          </p>
        </div>
        <div :class="[
          'flex justify-end items-center gap-x-4 hidden',
          'lg:(inline-flex)',
        ]">
          <span class="text-white/40 font-medium text-sm inline-block leading-[150%] text-right max-w-42.5">
            {{  t('faq_sub_desc') }}
          </span>
          <PrimaryButton href="/support">
            {{  t('contact_support') }}
          </PrimaryButton>
        </div>
      </div>
      <div class="basis-1/2 mt-7 flex flex-col lg:mt-0" >
        <div 
          v-for="(qa, index) in qas" 
          :key="index"
          class="w-full"
        > 
          <div 
            @click="selectFaq(index)"
            :class="[
              'w-full py-4 relative flex justify-between items-center',
              selectedFaq !== index && 'cursor-pointer'
            ]"
          >
            <button class="font-semibold text-white select-none" :disabled="selectedFaq === index" >
              {{ qa.question }}
            </button>
            <span 
              :class="[
                'text-xl text-white ease-in-out duration-300 inline-block',
                selectedFaq === index
                  ? 'i-material-symbols:keyboard-arrow-up opacity-100'
                  : 'i-material-symbols:keyboard-arrow-down opacity-80'
              ]" 
            />
            <div 
              v-if="selectedFaq !== index" 
              class="bg-brand-stroke/10 h-.5 w-full absolute bottom-0" 
            />
          </div>
          <!-- description -->
          <div 
            class="grid isolate relative w-full h-auto overflow-hidden duration-300 ease-in-out" 
            :class="[
              selectedFaq === index
                ? 'grid-rows-[1fr]'
                : 'grid-rows-[0fr]'
            ]"
          >
            <div 
              class="leading-[150%] text-brand-secondary prose prose-overrides max-w-none overflow-hidden"
              v-html="qa.answer"
            >
            </div>            
          </div>
          <div v-if="selectedFaq === index" class="bg-brand-stroke/10 h-.5 w-full mt-6" />
        </div>
      </div>
      <div :class="[
        'flex justify-center items-center flex-col gap-y-6 gap-x-6 mt-10',
        'sm:(flex-row)',
        'lg:(hidden)',
      ]">
        <span class="text-sm text-white/40 font-medium leading-[150%] xs:max-w-42.5 max-w-50 sm:text-right text-center">
            {{  t('faq_sub_desc') }}
        </span>
        <PrimaryButton href="/support">
          {{  t('contact_support') }}
        </PrimaryButton>
      </div>
    </div>
  </section>
</template>
